import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Loader2, ShieldAlert, Home } from "lucide-react";
import { HandoverWalkthroughSection } from "./handover/HandoverPublicParts";
import { SignOffPanel, SignedThankYou, PreviousSignOffNote } from "./handover/ClientSignOff";

export default function HandoverPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback((refresh = false) => fetch(`${API}/handover-public/${token}${refresh ? "?refresh=true" : ""}`)
    .then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "This link is no longer active. Please contact your builder.");
      setData(d);
    })
    .catch((e) => setError(e.message)), [token]);

  useEffect(() => { load(); }, [load]);
  // Live walkthroughs refresh quietly while the page is open
  useEffect(() => {
    const live = data?.dwellings?.some((d) => d.walkthroughs.some((w) => w.status === "in_progress"));
    if (!live) return;
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [data, load]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" data-testid="handover-public-error">
        <div className="max-w-sm text-center space-y-3">
          <ShieldAlert className="w-10 h-10 text-amber-600 mx-auto" />
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><Loader2 className="w-7 h-7 animate-spin text-amber-600" /></div>;

  const { builder, client } = data;
  const allSigned = data.dwellings.length > 0 && data.dwellings.every((d) => d.signoff.state === "signed");
  return (
    <div className="min-h-screen bg-stone-50" data-testid="handover-public-page">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center gap-3">
          {builder.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${builder.logo_url}`} alt="" className="h-10 w-10 rounded bg-white object-contain p-1" />}
          <div className="min-w-0">
            <p className="font-bold text-lg truncate" data-testid="handover-builder-name">{builder.company || "Your Builder"}</p>
            <p className="text-xs text-slate-300 tracking-wide uppercase">Client Handover</p>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="handover-title">{allSigned ? "Handover Complete" : "Your Handover Items"}</h1>
          <p className="text-slate-600 mt-1" data-testid="handover-client-line">
            {client.company_name ? `${client.company_name} — ` : ""}{client.name} · {data.site_name}
          </p>
        </div>
        {data.dwellings.length === 0 ? (
          <p className="text-slate-500" data-testid="handover-public-empty">No walkthrough items have been recorded yet.</p>
        ) : allSigned ? (
          <SignedThankYou signoff={data.dwellings.map((d) => d.signoff).sort((a, b) => b.signed_at.localeCompare(a.signed_at))[0]} dwelling={data.dwellings.length === 1 ? data.dwellings[0].dwelling_number : null} testId="handover-thank-you" />
        ) : data.dwellings.map((d) => (
          <section key={d.dwelling_number} className="space-y-4" data-testid={`handover-dwelling-${d.dwelling_number}`}>
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 border-b border-slate-200 pb-2">
              <Home className="w-5 h-5 text-amber-600" />{d.dwelling_number}
            </h2>
            {d.signoff.state === "signed" ? (
              <SignedThankYou signoff={d.signoff} dwelling={d.dwelling_number} compact testId={`handover-signed-${d.dwelling_number}`} />
            ) : (
              <>
                {d.signoff.state === "ready" && <SignOffPanel token={token} dwelling={d} defaultName={client.name} onSigned={() => load(true)} />}
                {d.signoff.state === "open" && d.signoff.signed_at && <PreviousSignOffNote signoff={d.signoff} testId={`handover-prev-signoff-${d.dwelling_number}`} />}
                {d.walkthroughs.map((w) => <HandoverWalkthroughSection key={w.sequence} w={w} dwelling={d.dwelling_number} />)}
              </>
            )}
          </section>
        ))}
        <footer className="pt-6 text-center text-xs text-slate-600 space-y-1">
          {(builder.phone || builder.email) && <p>Questions? Contact {builder.company || "your builder"}{builder.phone ? ` · ${builder.phone}` : ""}{builder.email ? ` · ${builder.email}` : ""}</p>}
          <p>Powered by <a href="https://siteproapp.au" className="text-amber-700 font-medium">Site Pro App</a></p>
        </footer>
      </main>
    </div>
  );
}
